import { APP_PREFIX_PATH } from "../configs/AppConfig";


export const getAuthenticatedEntry = (user) => {
  if (!user) {
    return `${APP_PREFIX_PATH}/brand-store`;
  }

  // Catalog is the home page — send users there first when they have access.
  if (hasCatalogAccess(user)) {
    return `${APP_PREFIX_PATH}/catalog`;
  }

  if (hasBrandStoreAccess(user)) {
    return `${APP_PREFIX_PATH}/brand-store`;
  }

  if (hasWardrobeAccess(user)) {
    return `${APP_PREFIX_PATH}/dashboard`;
  }

  return `${APP_PREFIX_PATH}/brand-store`;
};


export const getFallbackPath = (user) => {
  return getAuthenticatedEntry(user);
};

export const hasBrandStoreAccess = (user) => {
  if (!user) return true;
  if (user.is_sunday_super_admin) return true;
  if (user.current_company?.has_brand_store_access === false) return false;
  return user.has_brand_store_access !== false;
};

export const hasWardrobeAccess = (user) => {
  if (!user) return true;
  if (user.is_sunday_super_admin) return true;
  if (user.current_company?.has_wardrobe_access === false) return false;
  return user.has_wardrobe_access !== false;
};

export const hasRoleAccess = (user, requiredRoles, allowClosetAdmin = false) => {
  if (!requiredRoles || requiredRoles.length === 0) return true;
  if (!user) return false;
  if (user.is_sunday_super_admin) return true;
  const userRoles = user.role?.map((r) => r.name) || [];
  if (allowClosetAdmin && user.is_closet_admin && userRoles.includes("STAFF")) return true;
  return requiredRoles.some((role) => userRoles.includes(role));
};

export const hasCatalogAccess = (user) => {
  if (!user) return true;
  if (user.is_sunday_super_admin) return true;
  if (user.current_company?.has_catalog_access === false) return false;
  return user.has_catalog_access !== false;
};

// Custom shipment creation — USER-ONLY flag (no company counterpart). BE defaults
// the column to true, so access is only denied when the flag is explicitly false.
// NO super-admin bypass: the flag applies to everyone, super admins included —
// whoever has it can create, whoever doesn't can't.
// Gates ONLY the creation flow; viewing/editing existing custom shipments stays open.
export const hasCustomShipmentAccess = (user) => {
  if (!user) return true;
  return user.has_custom_shipment_access !== false;
};

// Company swag store — a locked teaser/placeholder in the app menu. Access is
// OFF for everyone until BE explicitly sets `has_company_swag_store_access`
// (on the user or current_company). No super-admin bypass on purpose: while it
// is a placeholder with no route, the item must stay locked (opens the pitch
// modal) for everyone — including Sunday admins.
export const hasSwagStoreAccess = (user) =>
  user?.has_company_swag_store_access === true ||
  user?.current_company?.has_company_swag_store_access === true;

// Request Forms (internal request / IR) — limited rollout: the nav section is
// visible only for Sunday (company 1) and Cloudflare (company 30).
const REQUEST_FORMS_COMPANY_IDS = [1, 30];

export const hasRequestFormsAccess = (user) => {
  const companyId = user?.current_company_id ?? user?.current_company?.id;
  return REQUEST_FORMS_COMPANY_IDS.includes(Number(companyId));
};

// Campaign burner links — internal tooling, visible only while the acting company
// is Nexo. Keyed on the company id (stable across renames) and on the CURRENT
// company (not is_sunday_super_admin, which persists across a company switch), so
// the page follows the company the user is acting as, matching the API gate.
export const NEXO_COMPANY_ID = 1455;
export const isNexoCompany = (user) =>
  Number(user?.current_company?.id) === NEXO_COMPANY_ID;

// Agency access — true if user is on agency company OR belongs to agency (persists across switch).
// Per BE: is_agency flips with current_company; is_from_agency stays true for users that belong
// to an agency, even after switching to a managed child company.
export const hasAgencyAccess = (user) =>
  Boolean(user?.is_agency || user?.is_from_agency);

// Commission visibility — when BE returns hide_commission=true, every commission/rebate
// figure (table columns, cards, charts, drawers) must be hidden across /agency/*.
// The flag exists on the user root and nested on current_company.
export const isCommissionHidden = (user) => {
  const flag = user?.hide_commission ?? user?.current_company?.hide_commission;
  return flag === true;
};
